import { useEffect, useState } from "react";
import { Landmark, FileText, Wallet, CalendarDays } from "lucide-react";
import { api, descargarPdf } from "@/lib/api";
import { money, fecha } from "@/lib/format";
import { Loading, PageTitle, Stat, Empty } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

export default function MisPagosNomina() {
  const [datos, setDatos] = useState(null);

  useEffect(() => {
    api.get("/nomina/mis-pagos").then((r) => setDatos(r.data))
      .catch(() => setDatos({ empleado: null, pagos: [], total_pagado: 0 }));
  }, []);

  const volante = async (p) => {
    try {
      await descargarPdf(`/nomina/mis-pagos/${p.periodo_id}/volante`, {},
                         `Volante-${p.desde || p.fecha_pago}.pdf`);
    } catch {
      toast.error("No se pudo generar su volante");
    }
  };

  if (!datos) return <Loading />;

  if (!datos.empleado) {
    return (
      <div data-testid="mis-pagos-nomina">
        <PageTitle title="Mis pagos de nómina"
                   subtitle="Aquí verá cada pago procesado por administración y su volante." />
        <div className="rounded-lg border border-border/60 bg-card p-10 text-center"
             data-testid="nomina-sin-ficha">
          <Landmark className="mx-auto mb-4 h-10 w-10 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            {datos.aviso || "Su cuenta aún no está vinculada a una ficha de empleado en Nómina."}
          </p>
        </div>
      </div>
    );
  }

  const ultimo = datos.pagos[0];

  return (
    <div data-testid="mis-pagos-nomina">
      <PageTitle title="Mis pagos de nómina"
                 subtitle="Cada pago procesado por administración, con su volante descargable en PDF." />

      <div className="mb-8 grid gap-4 sm:grid-cols-3">
        <Stat label="Pagos recibidos" value={datos.pagos.length} icon={Wallet}
              testId="nomina-cantidad-pagos" />
        <Stat label="Total recibido" value={money(datos.total_pagado)} tone="good"
              icon={Landmark} testId="nomina-total-recibido" />
        <Stat label="Último pago" value={ultimo ? money(ultimo.neto) : "—"} icon={CalendarDays}
              hint={ultimo ? `${fecha(ultimo.fecha_pago)} · ${ultimo.metodo}` : "Sin pagos aún"}
              testId="nomina-ultimo-pago" />
      </div>

      <p className="mb-4 text-sm text-muted-foreground" data-testid="nomina-ficha">
        {datos.empleado.nombre} · {datos.empleado.cargo}
        {datos.empleado.banco ? ` · ${datos.empleado.banco}` : ""}
        {datos.empleado.cuenta_banco ? ` ${datos.empleado.cuenta_banco}` : ""}
      </p>

      <div className="overflow-hidden rounded-lg border border-border/60 bg-card">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm" data-testid="tabla-mis-pagos">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-6 py-3">Periodo</th>
                <th className="px-6 py-3">Pagado el</th>
                <th className="px-6 py-3">Devengado</th>
                <th className="px-6 py-3">Deducciones</th>
                <th className="px-6 py-3">Neto</th>
                <th className="px-6 py-3">Forma de pago</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {datos.pagos.map((p) => (
                <tr key={p.id} className="border-t border-border/60 hover:bg-muted/30"
                    data-testid={`mi-pago-${p.id}`}>
                  <td className="px-6 py-4">
                    <span className="block font-medium">{p.periodo_nombre}</span>
                    <span className="block text-xs text-muted-foreground">
                      {fecha(p.desde)} al {fecha(p.hasta)}
                    </span>
                  </td>
                  <td className="px-6 py-4 font-mono text-xs">{fecha(p.fecha_pago)}</td>
                  <td className="px-6 py-4 font-mono text-xs">{money(p.total_ingresos)}</td>
                  <td className="px-6 py-4 font-mono text-xs">{money(p.total_deducciones)}</td>
                  <td className="px-6 py-4 font-mono text-sm font-semibold">{money(p.neto)}</td>
                  <td className="px-6 py-4">
                    <Badge variant="secondary" className="capitalize">{p.metodo}</Badge>
                    {p.referencia && (
                      <span className="mt-1 block font-mono text-[10px] text-muted-foreground">
                        ref. {p.referencia}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <Button size="sm" variant="outline" className="rounded-full"
                            onClick={() => volante(p)} data-testid={`descargar-volante-${p.id}`}>
                      <FileText className="mr-1 h-3 w-3" /> Volante
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {datos.pagos.length === 0 && (
          <Empty testId="mis-pagos-vacios">
            Todavía no hay pagos de nómina registrados a su nombre. Cuando administración cierre
            el periodo, su volante aparecerá aquí.
          </Empty>
        )}
      </div>
    </div>
  );
}
